import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import SEO from "../components/SEO";

const API_BASE = "https://eluvent.com/api";

const BoardVault = () => {
  const navigate = useNavigate();
  const { board: paramBoard, subject: paramSubject } = useParams();

  const [boards, setBoards] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedBoard, setSelectedBoard] = useState(paramBoard || "");
  const [selectedSubject, setSelectedSubject] = useState(paramSubject || "");
  const [subjectCounts, setSubjectCounts] = useState({});
  const [allSubjectCounts, setAllSubjectCounts] = useState({});
  const [questionOfDay, setQuestionOfDay] = useState(null);
  const [boardSubjects, setBoardSubjects] = useState({});
  const [boardDescription, setBoardDescription] = useState("");

  // Load boards
  useEffect(() => {
    axios.get(`${API_BASE}/vault/boards`)
      .then(res => {
        const list = res.data.boards || [];
        setBoards(list);
        list.forEach(b => {
          axios.get(`${API_BASE}/vault/subjects?board=${b.code}`)
            .then(subRes => setBoardSubjects(prev => ({ ...prev, [b.code]: subRes.data.subjects || [] })))
            .catch(() => {});
        });
      })
      .catch(() => {});
  }, []);

  // Load all subject counts for dashboard
  useEffect(() => {
    boards.forEach(b => {
      axios.get(`${API_BASE}/vault/subjects?board=${b.code}`)
        .then(res => {
          const subs = res.data.subjects || [];
          subs.forEach(s => {
            axios.get(`${API_BASE}/vault/questions?board=${b.code}&subject=${s.code}&limit=1`)
              .then(r => setAllSubjectCounts(prev => ({ ...prev, [`${b.code}_${s.code}`]: r.data.total || 0 })))
              .catch(() => {});
          });
        })
        .catch(() => {});
    });
  }, [boards]);

  // Question of the day
  useEffect(() => {
    axios.get(`${API_BASE}/vault/question-of-day`)
      .then(res => setQuestionOfDay(res.data.question))
      .catch(() => {});
  }, []);

  // Load subjects when board changes
  useEffect(() => {
    if (!selectedBoard) return;
    axios.get(`${API_BASE}/vault/subjects?board=${selectedBoard}`)
      .then(res => {
        const subs = res.data.subjects || [];
        setSubjects(subs);
        subs.forEach(s => {
          axios.get(`${API_BASE}/vault/questions?board=${selectedBoard}&subject=${s.code}&limit=1`)
            .then(r => setSubjectCounts(prev => ({ ...prev, [s.code]: r.data.total || 0 })));
        });
        if (subs.length > 0 && subs[0].description) {
          setBoardDescription(subs[0].description);
        }
      })
      .catch(() => {});
  }, [selectedBoard]);

  // Fetch questions for subject view
  const fetchQuestions = useCallback(() => {
    if (!selectedBoard || !selectedSubject) return;
    setLoading(true);
    const url = `${API_BASE}/vault/questions?board=${selectedBoard}&subject=${selectedSubject}&page=${page}&limit=20`;
    axios.get(url)
      .then(res => {
        setQuestions(res.data.questions || []);
        setTotalPages(Math.ceil((res.data.total || 0) / 20));
      })
      .catch(() => setQuestions([]))
      .finally(() => setLoading(false));
  }, [selectedBoard, selectedSubject, page]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  // Keep state in sync with URL parameters
  useEffect(() => {
    if (paramBoard) setSelectedBoard(paramBoard);
    if (paramSubject) setSelectedSubject(paramSubject);
  }, [paramBoard, paramSubject]);

  const handleBoardClick = (code) => {
    setSelectedBoard(code);
    setSelectedSubject("");
    setPage(1);
    navigate(`/board-vault/${code}`);
  };

  const handleSubjectClick = (code) => {
    setSelectedSubject(code);
    setPage(1);
    navigate(`/board-vault/${selectedBoard}/${code}`);
  };

  const goToQuestion = (qid) => {
    const ids = questions.map(q => q.id);
    const idx = ids.indexOf(qid);
    navigate(`/board-vault/question/${qid}`, {
      state: { questionIds: ids, currentIndex: idx >= 0 ? idx : 0, board: selectedBoard, subject: selectedSubject || "" }
    });
  };

  // ========== DASHBOARD (no board) ==========
  if (!selectedBoard) {
    return (
      <div style={{ maxWidth: 1000, margin: "0 auto", padding: 20, fontFamily: "'Nunito', sans-serif" }}>
        <SEO
          title="Board Vault – CBSE & ICSE Previous Year Questions"
          description="Practice CBSE and ICSE previous year questions with AI explanations. Thousands of questions for Class 10 Physics, Chemistry, Maths, Biology and more."
          canonical="https://eluvent.com/board-vault"
        />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <Link to="/" style={{ color: "#6366f1", textDecoration: "none", fontWeight: 700, fontSize: 14 }}>🏠 Home</Link>
        </div>
        <div style={{ background: "linear-gradient(135deg, #1e3a5f 0%, #2c5282 100%)", borderRadius: 24, padding: "40px 32px", marginBottom: 30, color: "white", textAlign: "center" }}>
          <h1 style={{ fontSize: 36, fontWeight: 800, margin: "0 0 12px" }}>📚 Board Vault</h1>
          <p style={{ fontSize: 18, opacity: 0.9, margin: "0 0 24px" }}>
            Practice previous year questions with AI‑powered explanations.<br />
            <strong>Not ChatGPT – trained exclusively for CBSE & ICSE syllabus.</strong>
          </p>
          <Link to="/" style={{ background: "#f59e0b", color: "white", padding: "14px 28px", borderRadius: 30, fontWeight: 700, fontSize: 16, textDecoration: "none" }}>🤖 Ask a Doubt Now</Link>
        </div>
        {questionOfDay && (
          <div style={{ background: "#fef3c7", borderRadius: 16, padding: "20px 24px", marginBottom: 30, border: "2px solid #fcd34d" }}>
            <h3 style={{ margin: "0 0 8px", color: "#92400e", fontSize: 18 }}>🌟 Question of the Day</h3>
            <p style={{ fontSize: 14, color: "#1e293b", margin: "0 0 12px" }}>{questionOfDay.question_text?.substring(0, 200)}...</p>
            <Link to={`/board-vault/question/${questionOfDay.id}`} style={{ display: "inline-block", background: "#f59e0b", color: "white", padding: "8px 20px", borderRadius: 20, fontWeight: 700, fontSize: 13, textDecoration: "none" }}>Solve Now →</Link>
          </div>
        )}
        <h2 style={{ margin: "0 0 16px", color: "#1e3a5f" }}>Choose Your Board</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
          {boards.map(b => {
            const subs = boardSubjects[b.code] || [];
            const total = subs.reduce((sum, s) => sum + (allSubjectCounts[`${b.code}_${s.code}`] || 0), 0);
            return (
              <div key={b.code}
                onClick={() => handleBoardClick(b.code)}
                style={{
                  background: "white", borderRadius: 16, padding: "24px 20px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  cursor: "pointer", transition: "transform 0.2s, box-shadow 0.2s", border: "2px solid #e2e8f0"
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.1)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.06)"; }}
              >
                <h3 style={{ margin: "0 0 8px", color: "#1e3a5f", fontSize: 20 }}>{b.name}</h3>
                <p style={{ margin: "0 0 12px", color: "#64748b", fontSize: 14 }}>{total} questions available</p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {subs.map(s => (
                    <span key={s.code} style={{ background: "#f1f5f9", borderRadius: 20, padding: "4px 12px", fontSize: 12, color: "#475569", fontWeight: 600 }}>
                      {s.name} ({allSubjectCounts[`${b.code}_${s.code}`] || 0})
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 40, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
          <p>Eluvent AI is trained exclusively on CBSE & ICSE syllabi. Not a generic AI like ChatGPT.</p>
          <Link to="/" style={{ color: "#6366f1", fontWeight: 700 }}>Ask your first doubt →</Link>
        </div>
      </div>
    );
  }

  // ========== BOARD SELECTED (no subject) – Subject Cards ==========
  if (selectedBoard && !selectedSubject) {
    const currentBoard = boards.find(b => b.code === selectedBoard) || {};
    const subs = boardSubjects[selectedBoard] || [];
    const total = subs.reduce((sum, s) => sum + (allSubjectCounts[`${selectedBoard}_${s.code}`] || 0), 0);

    return (
      <div style={{ maxWidth: 1000, margin: "0 auto", padding: 20, fontFamily: "'Nunito', sans-serif" }}>
        <SEO
          title={`${currentBoard.name} Board – Previous Year Questions`}
          description={boardDescription || `Practice ${currentBoard.name} previous year questions with AI explanations.`}
          canonical={`https://eluvent.com/board-vault/${selectedBoard}`}
        />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 12 }}>
            <Link to="/board-vault" style={{ color: "#6366f1", fontWeight: 600, fontSize: 13 }}>← All Boards</Link>
            <Link to="/" style={{ color: "#6366f1", fontWeight: 600, fontSize: 13 }}>🏠 Home</Link>
          </div>
        </div>
        <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)", borderRadius: 20, padding: "32px 24px", marginTop: 16, marginBottom: 24, color: "white" }}>
          <h1 style={{ margin: "0 0 8px", fontSize: 28, fontWeight: 800 }}>{currentBoard.name}</h1>
          {boardDescription && <p style={{ margin: "0 0 16px", fontSize: 15, opacity: 0.9, lineHeight: 1.6 }}>{boardDescription}</p>}
          <p style={{ margin: 0, fontSize: 14 }}><strong>{total}</strong> questions across {subs.length} subjects</p>
          <Link to="/" style={{ display: "inline-block", marginTop: 16, background: "#f59e0b", color: "white", padding: "10px 24px", borderRadius: 25, fontWeight: 700, fontSize: 14, textDecoration: "none" }}>🤖 Ask a Doubt Now</Link>
        </div>

        <h2 style={{ margin: "0 0 16px", color: "#1e3a5f" }}>Subjects</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16 }}>
          {subs.map(s => (
            <div key={s.code}
              onClick={() => handleSubjectClick(s.code)}
              style={{ background: "white", borderRadius: 16, padding: "20px 16px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", cursor: "pointer", border: "2px solid #e2e8f0" }}
            >
              <h3 style={{ margin: "0 0 8px", fontSize: 18, color: "#1e293b" }}>{s.name}</h3>
              <p style={{ margin: "0 0 12px", color: "#64748b", fontSize: 14 }}>{allSubjectCounts[`${selectedBoard}_${s.code}`] || 0} questions</p>
              <span style={{ color: "#6366f1", fontWeight: 700, fontSize: 13 }}>Practice →</span>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 40, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
          <p>Eluvent AI is trained exclusively on CBSE & ICSE syllabi. Not a generic AI like ChatGPT.</p>
          <Link to="/" style={{ color: "#6366f1", fontWeight: 700 }}>Ask your first doubt →</Link>
        </div>
      </div>
    );
  }

  // ========== SUBJECT SELECTED (with pagination) ==========
  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: 20, fontFamily: "'Nunito', sans-serif" }}>
      <SEO
        title={`${selectedSubject} ${selectedBoard} – Board Vault`}
        description={`Practice ${selectedSubject} questions for ${selectedBoard} Class 10. Instant AI explanations and similar questions.`}
        canonical={window.location.href}
      />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <Link to="/" style={{ color: "#6366f1", fontWeight: 600, fontSize: 13 }}>🏠 Home</Link>
      </div>
      <h1 style={{ textAlign: "center", color: "#1e3a5f" }}>📚 Board Vault</h1>

      {/* Board selection */}
      <div style={{ margin: "20px 0" }}>
        <h3>Select Board</h3>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {boards.map(b => (
            <button key={b.code}
              onClick={() => handleBoardClick(b.code)}
              style={{
                padding: "10px 20px", borderRadius: 25, border: "2px solid #6366f1",
                background: selectedBoard === b.code ? "#6366f1" : "white",
                color: selectedBoard === b.code ? "white" : "#6366f1", fontWeight: "700", cursor: "pointer"
              }}
            >{b.name} ({b.code})</button>
          ))}
        </div>
      </div>

      {/* Subject selection */}
      {selectedBoard && (
        <div style={{ margin: "20px 0" }}>
          <h3>Select Subject</h3>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {subjects.map(s => (
              <button key={s.code}
                onClick={() => handleSubjectClick(s.code)}
                style={{
                  padding: "10px 20px", borderRadius: 25, border: "2px solid #10b981",
                  background: selectedSubject === s.code ? "#10b981" : "white",
                  color: selectedSubject === s.code ? "white" : "#10b981", fontWeight: "700", cursor: "pointer"
                }}
              >{s.name} ({subjectCounts[s.code] !== undefined ? subjectCounts[s.code] : "..."})</button>
            ))}
          </div>
        </div>
      )}

      {/* Help info */}
      {selectedBoard && selectedSubject && (
        <div style={{ background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)", borderRadius: 16, padding: "16px 20px", marginBottom: 20, border: "1px solid #bae6fd", fontSize: 14, color: "#0c4a6e", lineHeight: 1.6 }}>
          <strong style={{ fontSize: 16 }}>📘 Need help with a question?</strong>
          <p style={{ margin: "8px 0 0" }}>If any question looks incomplete or you're stuck:</p>
          <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
            <li>🤖 <strong>AI Explanation</strong> – get step‑by‑step guidance.</li>
            <li>🔄 <strong>Similar Questions</strong> – practice with variants.</li>
            <li>📝 <strong>Practice Test</strong> – generate a full test on this topic.</li>
          </ul>
          <p style={{ margin: "8px 0 0", fontStyle: "italic" }}>Just click on any question to open these tools. We’re constantly improving our content!</p>
        </div>
      )}

      {/* Questions list with pagination */}
      {selectedSubject && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "10px 0", background: "#f8fafc", padding: "8px 12px", borderRadius: 20 }}>
            <h3 style={{ margin: 0 }}>Questions</h3>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                style={{ padding: "6px 15px", borderRadius: 20, border: "1px solid #6366f1", background: "white", color: "#6366f1", cursor: page <= 1 ? "not-allowed" : "pointer" }}
              >← Prev</button>
              <span style={{ fontWeight: 600 }}>Page {page} of {totalPages || 1}</span>
              <button
                disabled={page >= (totalPages || 1)}
                onClick={() => setPage(p => p + 1)}
                style={{ padding: "6px 15px", borderRadius: 20, border: "1px solid #6366f1", background: "white", color: "#6366f1", cursor: page >= (totalPages || 1) ? "not-allowed" : "pointer" }}
              >Next →</button>
            </div>
          </div>
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0 }}>
              {questions.map(q => (
                <li key={q.id} style={{ background: "white", padding: 12, marginBottom: 8, borderRadius: 8, boxShadow: "0 2px 4px rgba(0,0,0,0.05)", cursor: "pointer" }}
                  onClick={() => goToQuestion(q.id)}>
                  <strong>Q{q.question_number}:</strong> {q.question_text?.substring(0, 150)}...
                  <div style={{ fontSize: 12, color: "#64748b" }}>Marks: {q.marks} | Type: {q.question_type} | Year: {q.year}</div>
                </li>
              ))}
              {questions.length === 0 && !loading && <p>No questions found in this subject.</p>}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default BoardVault;